import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Banknote, CheckCircle2, CreditCard, Download, Info, Landmark, RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/apiError";

const R = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  const [whole, decimal] = Math.abs(v).toFixed(2).split(".");
  return `${sign}R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS = [
  { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" },
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "month", label: "This month" },
  { v: "last_month", label: "Last month" },
];
const presetBounds = (p) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const d = (n) => { const x = new Date(t); x.setDate(x.getDate() + n); return x; };
  switch (p) {
    case "yesterday": return [isoDate(d(-1)), isoDate(d(-1))];
    case "7d": return [isoDate(d(-6)), isoDate(t)];
    case "30d": return [isoDate(d(-29)), isoDate(t)];
    case "month": return [isoDate(new Date(t.getFullYear(), t.getMonth(), 1)), isoDate(t)];
    case "last_month": return [isoDate(new Date(t.getFullYear(), t.getMonth() - 1, 1)), isoDate(new Date(t.getFullYear(), t.getMonth(), 0))];
    default: return [isoDate(t), isoDate(t)];
  }
};
const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
};

/**
 * Session 74 — Finance → Bank Reconciliation.
 * "What should be in the bank, and what should the cash have added up to,
 * for this window?" Every number comes from /api/owner/reports/bank-reconciliation,
 * which uses the SAME money function as the Operations card and the emailed
 * payment report. Day rows are what the owner ticks off against the
 * statement; the panel only presents.
 */
export const BankReconciliationPanel = ({ http, authHeaders, onSendToSpreadsheet }) => {
  const [preset, setPreset] = useState("7d");
  const [custom, setCustom] = useState(() => { const [s, e] = presetBounds("7d"); return { start: s, end: e }; });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hideEmpty, setHideEmpty] = useState(true);

  const bounds = useMemo(() => (preset === "custom" ? [custom.start, custom.end] : presetBounds(preset)), [preset, custom]);

  const load = useCallback(async () => {
    const [start, end] = bounds;
    if (!start || !end) return;
    setLoading(true);
    setError("");
    try {
      const r = await http.get("/owner/reports/bank-reconciliation", { headers: authHeaders, params: { start_date: start, end_date: end }, timeout: 90000 });
      setReport(r.data);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the bank reconciliation."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, bounds]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = report?.days || [];
    return hideEmpty && all.length > 1 ? all.filter((d) => !d.empty) : all;
  }, [report, hideEmpty]);
  const hasCashUps = (report?.cash?.cash_ups?.count || 0) > 0;
  const hasCashOut = (report?.cash?.cash_out || 0) > 0;

  const exportRows = () => {
    if (!report || !onSendToSpreadsheet) return;
    const out = [
      ["Bank reconciliation", `${report.range.start_local} to ${report.range.end_local}`],
      [],
      ["Expected in bank", R(report.bank.total)],
      ["  Card sales", report.bank.card_sales], ["  Card tips", report.bank.card_tips], ["  EFT sales", report.bank.eft_sales], ["  EFT tips", report.bank.eft_tips],
      [],
      ["Cash received (sales + tips)", report.cash.gross], ["  Cash sales", report.cash.sales], ["  Cash tips", report.cash.tips],
      ["  Paid out of the drawer", -(report.cash.cash_out || 0)], ["Cash expected after payouts", report.cash.net_expected],
      hasCashUps ? ["Cash counted at cash-up", report.cash.cash_ups.counted] : null,
      hasCashUps ? ["Cash-up variance", report.cash.cash_ups.variance] : null,
      [],
      ["Date", "Card", "Card tips", "EFT", "EFT tips", "Bank total", "Cash", "Cash tips", "Paid out", "Cash net", "Counted", "Variance", "Bills settled"],
      ...(report.days || []).map((d) => [d.date, d.card, d.card_tips, d.eft, d.eft_tips, d.bank_total, d.cash, d.cash_tips, -(d.cash_payouts + d.cash_supplier), d.cash_net, d.cash_counted ?? "", d.cash_variance ?? "", d.bills_settled]),
    ].filter(Boolean);
    onSendToSpreadsheet(out, `Bank reconciliation ${report.range.start_local}`);
  };

  return (
    <div className="space-y-4" data-testid="bank-recon-panel">
      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2" data-testid="bank-recon-range">
        {PRESETS.map((p) => (
          <Button key={p.v} type="button" size="sm" variant={preset === p.v ? "default" : "outline"} className="rounded-full" onClick={() => setPreset(p.v)} data-testid={`bank-recon-preset-${p.v}`}>{p.label}</Button>
        ))}
        <div className="flex items-center gap-1.5">
          <Input type="date" value={custom.start} max={custom.end} onChange={(e) => { setCustom((c) => ({ ...c, start: e.target.value })); setPreset("custom"); }} className="h-8 w-auto rounded-full px-3 text-xs" data-testid="bank-recon-custom-start" aria-label="Start date" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={custom.end} min={custom.start} onChange={(e) => { setCustom((c) => ({ ...c, end: e.target.value })); setPreset("custom"); }} className="h-8 w-auto rounded-full px-3 text-xs" data-testid="bank-recon-custom-end" aria-label="End date" />
        </div>
        <Button type="button" size="sm" variant="ghost" className="ml-auto h-8" onClick={load} disabled={loading} data-testid="bank-recon-refresh">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {error ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-300/60 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200" data-testid="bank-recon-error">
          <span>{error}</span>
          <Button size="sm" variant="outline" className="h-7" onClick={load}>Retry</Button>
        </div>
      ) : null}

      {loading && !report ? (
        <div className="grid gap-3 md:grid-cols-3" data-testid="bank-recon-loading">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-[22px]" />)}
        </div>
      ) : report ? (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            {/* Expected in bank */}
            <Card className="rounded-[22px] border-sky-200 bg-sky-50/70 dark:border-sky-900/50 dark:bg-sky-950/30" data-testid="bank-recon-bank-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sky-800 dark:text-sky-200">
                  <Landmark className="h-4 w-4" />
                  <p className="text-[11px] font-semibold uppercase tracking-wide">Expected in the bank</p>
                </div>
                <p className="mt-2 text-3xl font-semibold text-sky-900 dark:text-sky-100" data-testid="bank-recon-bank-total">{R(report.bank.total)}</p>
                <dl className="mt-3 space-y-1 text-xs text-sky-900/80 dark:text-sky-100/80">
                  <div className="flex justify-between"><dt className="flex items-center gap-1"><CreditCard className="h-3 w-3" /> Card sales ({report.bank.bills.card} bills)</dt><dd data-testid="bank-recon-card-sales">{R(report.bank.card_sales)}</dd></div>
                  <div className="flex justify-between"><dt className="pl-4">Card tips</dt><dd data-testid="bank-recon-card-tips">{R(report.bank.card_tips)}</dd></div>
                  <div className="flex justify-between"><dt>EFT sales ({report.bank.bills.eft} bills)</dt><dd data-testid="bank-recon-eft-sales">{R(report.bank.eft_sales)}</dd></div>
                  <div className="flex justify-between"><dt className="pl-4">EFT tips</dt><dd data-testid="bank-recon-eft-tips">{R(report.bank.eft_tips)}</dd></div>
                  {report.bank.bills.split > 0 ? <p className="pt-1 text-[11px] opacity-80">{report.bank.bills.split} split bills — each leg is counted under the method it was paid with.</p> : null}
                </dl>
              </CardContent>
            </Card>

            {/* Expected cash */}
            <Card className="rounded-[22px] border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/50 dark:bg-emerald-950/30" data-testid="bank-recon-cash-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                  <Banknote className="h-4 w-4" />
                  <p className="text-[11px] font-semibold uppercase tracking-wide">Cash that should have been counted</p>
                </div>
                <p className="mt-2 text-3xl font-semibold text-emerald-900 dark:text-emerald-100" data-testid="bank-recon-cash-net">{R(report.cash.net_expected)}</p>
                <dl className="mt-3 space-y-1 text-xs text-emerald-900/80 dark:text-emerald-100/80">
                  <div className="flex justify-between"><dt>Cash sales ({report.cash.bills} bills)</dt><dd data-testid="bank-recon-cash-sales">{R(report.cash.sales)}</dd></div>
                  <div className="flex justify-between"><dt className="pl-4">Cash tips</dt><dd data-testid="bank-recon-cash-tips">{R(report.cash.tips)}</dd></div>
                  <div className="flex justify-between font-medium"><dt>Cash received</dt><dd data-testid="bank-recon-cash-gross">{R(report.cash.gross)}</dd></div>
                  {hasCashOut ? (
                    <>
                      {report.cash.payouts > 0 ? <div className="flex justify-between"><dt>Payouts from the drawer</dt><dd data-testid="bank-recon-cash-payouts">−{R(report.cash.payouts)}</dd></div> : null}
                      {report.cash.supplier_payments > 0 ? <div className="flex justify-between"><dt>Suppliers paid in cash</dt><dd data-testid="bank-recon-cash-supplier">−{R(report.cash.supplier_payments)}</dd></div> : null}
                    </>
                  ) : <p className="pt-1 text-[11px] opacity-80">No payouts recorded — the full cash received should be in the drawer / banked.</p>}
                  {hasCashUps ? (
                    <div className={`mt-2 rounded-lg border px-2 py-1.5 ${Math.abs(report.cash.cash_ups.variance) > 0.009 ? "border-amber-400/60 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100" : "border-emerald-300/60 bg-emerald-100/60 dark:bg-emerald-900/30"}`} data-testid="bank-recon-cashups">
                      <div className="flex justify-between"><span>Counted at {report.cash.cash_ups.count} cash-up{report.cash.cash_ups.count === 1 ? "" : "s"}</span><span>{R(report.cash.cash_ups.counted)}</span></div>
                      <div className="flex justify-between font-medium"><span>Variance</span><span data-testid="bank-recon-cashups-variance">{report.cash.cash_ups.variance >= 0 ? "+" : ""}{R(report.cash.cash_ups.variance)}</span></div>
                    </div>
                  ) : null}
                </dl>
              </CardContent>
            </Card>

            {/* Everything received */}
            <Card className="rounded-[22px] border-border bg-card" data-testid="bank-recon-total-card">
              <CardContent className="p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Total money received</p>
                <p className="mt-2 text-3xl font-semibold" data-testid="bank-recon-collected">{R(report.collected)}</p>
                <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between"><dt>Bank + cash (sales)</dt><dd>{R(report.bank.card_sales + report.bank.eft_sales + report.cash.sales)}</dd></div>
                  <div className="flex justify-between"><dt>Tips on top (all methods)</dt><dd data-testid="bank-recon-tips">{R(report.tips)}</dd></div>
                  <div className="flex justify-between"><dt>Bills settled · payments</dt><dd>{report.bills_settled} · {report.payments}</dd></div>
                  {report.vouchers > 0 ? <div className="flex justify-between"><dt>Vouchers used (not money)</dt><dd>{R(report.vouchers)}</dd></div> : null}
                  {report.outstanding_on_settled > 0 ? <div className="flex justify-between text-rose-700 dark:text-rose-300"><dt>Still owed on settled bills</dt><dd data-testid="bank-recon-outstanding">{R(report.outstanding_on_settled)}</dd></div> : null}
                  {report.partial_payment_tables > 0 ? <div className="flex justify-between"><dt>Tables with part-payments only</dt><dd>{report.partial_payment_tables}</dd></div> : null}
                </dl>
                <div className="mt-3 flex items-center gap-1.5 text-[11px]" data-testid="bank-recon-selfcheck">
                  {report.self_check?.bank_matches && report.self_check?.cash_matches ? (
                    <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-700 dark:text-emerald-300">Day rows add up to these totals to the cent.</span></>
                  ) : (
                    <><TriangleAlert className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700 dark:text-amber-300">Day rows do not match the totals — please report this.</span></>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-start gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground" data-testid="bank-recon-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>Money is counted on the day it was <strong>received</strong> (a table paid after midnight lands on the next day). Card and EFT tips go to the bank with the sale; cash tips stay in the drawer. Card-machine settlements usually reach your account one to two working days later — match them by transaction day. Cash expected = cash received minus payouts and supplier cash recorded in Cash Management.</p>
          </div>

          {/* Day-by-day */}
          <Card className="rounded-[22px] border-border bg-card">
            <CardContent className="p-0">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">Day by day</p>
                  <p className="text-[11px] text-muted-foreground">{report.range.start_local === report.range.end_local ? fmtDay(report.range.start_local) : `${fmtDay(report.range.start_local)} – ${fmtDay(report.range.end_local)}`} · {report.range.days} day{report.range.days === 1 ? "" : "s"} · venue time {report.range.timezone}</p>
                </div>
                <div className="flex items-center gap-3">
                  {(report.days || []).length > 1 ? (
                    <div className="flex items-center gap-1.5">
                      <Switch id="bank-recon-hide-empty" checked={hideEmpty} onCheckedChange={setHideEmpty} data-testid="bank-recon-hide-empty" />
                      <Label htmlFor="bank-recon-hide-empty" className="text-xs">Hide days with no money</Label>
                    </div>
                  ) : null}
                  {onSendToSpreadsheet ? (
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={exportRows} data-testid="bank-recon-export">
                      <Download className="mr-1 h-3.5 w-3.5" /> Send to spreadsheet
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="bank-recon-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Date</TableHead>
                      <TableHead className="whitespace-nowrap text-right">Card</TableHead>
                      <TableHead className="whitespace-nowrap text-right">EFT</TableHead>
                      <TableHead className="whitespace-nowrap text-right font-semibold text-sky-700 dark:text-sky-300">To bank</TableHead>
                      <TableHead className="whitespace-nowrap text-right">Cash</TableHead>
                      {hasCashOut ? <TableHead className="whitespace-nowrap text-right">Paid out</TableHead> : null}
                      <TableHead className="whitespace-nowrap text-right font-semibold text-emerald-700 dark:text-emerald-300">Cash expected</TableHead>
                      {hasCashUps ? <TableHead className="whitespace-nowrap text-right">Counted</TableHead> : null}
                      {hasCashUps ? <TableHead className="whitespace-nowrap text-right">Variance</TableHead> : null}
                      <TableHead className="whitespace-nowrap text-right">Bills</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground" data-testid="bank-recon-empty">No money was received in this window.</TableCell></TableRow>
                    ) : rows.map((d) => (
                      <TableRow key={d.date} className={d.empty ? "opacity-50" : ""} data-testid={`bank-recon-row-${d.date}`}>
                        <TableCell className="whitespace-nowrap text-sm"><span className="font-medium">{fmtDay(d.date)}</span> <span className="text-xs text-muted-foreground">{d.weekday}</span></TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">{R(d.card)}{d.card_tips > 0 ? <span className="block text-[10px] text-muted-foreground">+ tips {R(d.card_tips)}</span> : null}</TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">{R(d.eft)}{d.eft_tips > 0 ? <span className="block text-[10px] text-muted-foreground">+ tips {R(d.eft_tips)}</span> : null}</TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm font-semibold tabular-nums text-sky-800 dark:text-sky-200" data-testid={`bank-recon-row-bank-${d.date}`}>{R(d.bank_total)}</TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">{R(d.cash)}{d.cash_tips > 0 ? <span className="block text-[10px] text-muted-foreground">+ tips {R(d.cash_tips)}</span> : null}</TableCell>
                        {hasCashOut ? <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">{d.cash_payouts + d.cash_supplier > 0 ? `−${R(d.cash_payouts + d.cash_supplier)}` : "—"}</TableCell> : null}
                        <TableCell className={`whitespace-nowrap text-right text-sm font-semibold tabular-nums ${d.cash_net < 0 ? "text-rose-700 dark:text-rose-300" : "text-emerald-800 dark:text-emerald-200"}`} data-testid={`bank-recon-row-cash-${d.date}`}>{R(d.cash_net)}</TableCell>
                        {hasCashUps ? <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">{d.cash_counted == null ? "—" : R(d.cash_counted)}</TableCell> : null}
                        {hasCashUps ? <TableCell className={`whitespace-nowrap text-right text-sm tabular-nums ${d.cash_variance == null ? "text-muted-foreground" : Math.abs(d.cash_variance) > 0.009 ? "text-amber-700 dark:text-amber-300" : ""}`}>{d.cash_variance == null ? "—" : `${d.cash_variance >= 0 ? "+" : ""}${R(d.cash_variance)}`}</TableCell> : null}
                        <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">{d.bills_settled}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length > 1 ? (
                      <TableRow className="bg-secondary/50 font-semibold" data-testid="bank-recon-totals-row">
                        <TableCell className="text-sm">Total</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{R(report.bank.card_sales)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{R(report.bank.eft_sales)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-sky-800 dark:text-sky-200">{R(report.bank.total)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{R(report.cash.sales)}</TableCell>
                        {hasCashOut ? <TableCell className="text-right text-sm tabular-nums text-muted-foreground">−{R(report.cash.cash_out)}</TableCell> : null}
                        <TableCell className="text-right text-sm tabular-nums text-emerald-800 dark:text-emerald-200">{R(report.cash.net_expected)}</TableCell>
                        {hasCashUps ? <TableCell className="text-right text-sm tabular-nums">{R(report.cash.cash_ups.counted)}</TableCell> : null}
                        {hasCashUps ? <TableCell className="text-right text-sm tabular-nums">{report.cash.cash_ups.variance >= 0 ? "+" : ""}{R(report.cash.cash_ups.variance)}</TableCell> : null}
                        <TableCell className="text-right text-sm tabular-nums">{report.bills_settled}</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          {rows.length > 0 ? <p className="text-[11px] text-muted-foreground"><Badge variant="outline" className="mr-1 rounded-full text-[10px]">Tip</Badge>Bank totals above include card and EFT tips — compare them with your card-machine settlement report, which also includes tips.</p> : null}
        </>
      ) : null}
    </div>
  );
};

export default BankReconciliationPanel;
